import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import InputField from '../../components/ui/InputField';
import SelectField from '../../components/ui/SelectField';
import TextareaField from '../../components/ui/TextareaField';
import SingleDatePicker from '../../components/ui/SingleDatePicker';
import CustomButton from '../../components/CustomButton';
import { useEnterTenantContractMutation } from '../../api/account/accountApiSlice';
import { CONTRACT_TIERS, ContractTier, MasterTenantRow, OBJECT_LIMIT_MAX } from '../../api/account/accountApiTypes';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';
import { formatDate } from '../../utils';
import { stationCalendarDay } from '../../utils/stationTime';

const ISO_DATE = 'YYYY-MM-DD';
/** `tenant_contract.offer_number` is `VARCHAR(50)`. */
const OFFER_NUMBER_MAX = 50;

/** How an "Individuell" contract is limited: the master's explicit choice, or none yet. */
export type IndividualLimitChoice = 'limited' | 'unlimited' | null;

/**
 * The `objectLimit` an "Individuell" contract is sent with, or `undefined`
 * while the choice is incomplete (nothing chosen, or "Begrenzt" without a
 * whole number of at least 1). "Unbegrenzt" is only ever the master's choice,
 * never an empty field (PO, 2026-11-02).
 */
export const individualObjectLimit = (choice: IndividualLimitChoice, limitText: string): number | null | undefined => {
    if (choice === 'unlimited') return null;
    if (choice !== 'limited') return undefined;
    const text = limitText.trim();
    if (!/^\d+$/.test(text)) return undefined;
    const value = Number(text);
    // The server's bound (`isObjectLimitValue`): 1 to the INTEGER column's maximum.
    return Number.isSafeInteger(value) && value >= 1 && value <= OBJECT_LIMIT_MAX ? value : undefined;
};

/** The limit's number box; greyed while "Begrenzt" is not the choice. */
const limitInputClass = 'w-20 rounded-md border border-foreground/20 bg-text-contrast px-2 py-1 text-sm text-foreground'
    + ' disabled:bg-foreground/5 disabled:text-foreground/40';

interface TenantContractDialogProps {
    tenant: MasterTenantRow;
    onClose: () => void;
}

/**
 * Why the switch was refused, in the master's language: the refusals that have
 * a remedy on this dialog are translated with the server's numbers; anything
 * else shows the server's own message (`errorMessageFrom`). No response at all
 * is said as such — never an empty error.
 */
export const contractErrorMessage = (err: unknown, t: TFunction, language: 'de' | 'en'): string => {
    const status = (err as { status?: unknown } | undefined)?.status;
    if (typeof status !== 'number') return t('master.tenants.errors.unreachable');
    const data = (err as { data?: unknown }).data;
    const body = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
    switch (body.code) {
        case 'contract_below_count':
            // Rooms and stations together, as the tier counts them.
            return t('master.tenants.errors.contract_below_count', { max: body.max, current: body.current });
        case 'accepted_on_in_future':
        case 'starts_on_in_future':
        case 'object_limit_required':
        case 'invalid_object_limit':
        case 'object_limit_not_allowed':
        case 'tenant_archived':
        case 'tenant_not_active':
            return t(`master.tenants.errors.${body.code}`);
        case 'starts_before_current':
            return t('master.tenants.errors.starts_before_current', {
                currentStartsOn: typeof body.currentStartsOn === 'string' ? formatDate(body.currentStartsOn, language) : '',
            });
        default:
            break;
    }
    return errorMessageFrom(err, t('master.tenants.errors.generic'));
};

/**
 * "In Vertrag umstellen" / "Vertrag ändern" (plan tenant-management-first-step
 * §4): tier, offer number, acceptance date, start (default: the acceptance
 * date), note. "Individuell" asks for its limit: "Begrenzt auf n" or
 * "Unbegrenzt", nothing pre-selected, Save disabled until one is complete;
 * the standard tiers hide the choice (their tier is the limit). An action, not a setting — so its own buttons, like the users
 * dialog. Saving answers the tenant's new row, which the list shows at once
 * (`MasterTenants` is invalidated); a refusal stays in the dialog with the
 * server's reason.
 */
const TenantContractDialog = ({ tenant, onClose }: TenantContractDialogProps) => {
    const { t, i18n } = useTranslation();
    const language: 'de' | 'en' = i18n.language?.startsWith('en') ? 'en' : 'de';
    const dispatch = useDispatch();
    const [enterContract, { isLoading }] = useEnterTenantContractMutation();
    const today = stationCalendarDay(new Date());

    const [tier, setTier] = useState<ContractTier | ''>(tenant.contract?.tier ?? '');
    const [offerNumber, setOfferNumber] = useState('');
    const [acceptedOn, setAcceptedOn] = useState<string | null>(null);
    const [startsOn, setStartsOn] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [limitChoice, setLimitChoice] = useState<IndividualLimitChoice>(null);
    const [limitText, setLimitText] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // The start defaults to the acceptance date until one is picked.
    const shownStart = startsOn ?? acceptedOn;
    const isChange = !tenant.isTrial;
    const name = tenant.organizationName;
    const isIndividual = tier === 'individual';
    const objectLimit = isIndividual ? individualObjectLimit(limitChoice, limitText) : undefined;
    const limitInvalid = limitChoice === 'limited' && limitText.trim() !== '' && objectLimit === undefined;
    const canSubmit = !!tier && !!offerNumber.trim() && !!acceptedOn && acceptedOn <= today
        && (!startsOn || startsOn <= today)
        && (!isIndividual || objectLimit !== undefined);
    const tierLabels = Object.fromEntries(
        CONTRACT_TIERS.map((value) => [value, t(`master.tenants.tier.${value}`)]),
    ) as Record<string, string>;

    const handleSubmit = async () => {
        if (!canSubmit || !tier || !acceptedOn) return;
        setError(null);
        try {
            await enterContract({
                slug: tenant.slug,
                tier,
                ...(isIndividual ? { objectLimit } : {}),
                offerNumber: offerNumber.trim(),
                acceptedOn,
                ...(startsOn ? { startsOn } : {}),
                ...(note.trim() ? { note: note.trim() } : {}),
            }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('master.tenants.dialog.success'), autoHideDuration: 3000 }));
            onClose();
        } catch (err) {
            setError(contractErrorMessage(err, t, language));
        }
    };

    // Portalled to <body> above the sidebar, like the other dialogs.
    return createPortal(
        <div className={'fixed inset-0 z-[1300] flex items-center justify-center p-4'}>
            <div className={'fixed inset-0 bg-black bg-opacity-50'} onClick={onClose} />

            <div
                className={'relative z-10 bg-text-contrast rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto'}
                role={'dialog'}
                aria-modal={'true'}
            >
                <div className={'p-6'}>
                    <h3 className={'text-lg font-semibold text-foreground mb-1'}>
                        {t(isChange ? 'master.tenants.dialog.title_change' : 'master.tenants.dialog.title_switch', { name })}
                    </h3>
                    <p className={'text-sm text-foreground/60 mb-1'}>
                        {t(isChange ? 'master.tenants.dialog.description_change' : 'master.tenants.dialog.description_switch')}
                    </p>
                    <p className={'text-sm text-foreground/60 mb-5'}>
                        {/* Both counts, as the server reports them; a tier limits their sum (PO, 2026-11-02). */}
                        {t('master.tenants.dialog.current_counts', { rooms: tenant.counts.rooms, stations: tenant.counts.stations })}
                    </p>

                    <div className={'flex flex-col gap-4'}>
                        <SelectField
                            label={t('master.tenants.dialog.tier') + ' *'}
                            value={tier}
                            onChange={(value) => {
                                setTier(value as ContractTier);
                                // A new tier asks afresh: no limit choice carried over.
                                setLimitChoice(null);
                                setLimitText('');
                            }}
                            options={[...CONTRACT_TIERS]}
                            optionLabels={tierLabels}
                            labelAbove
                        />
                        {isIndividual && (
                            <fieldset>
                                <legend className={'mb-2 text-sm font-medium text-foreground'}>
                                    {t('master.tenants.dialog.limit') + ' *'}
                                </legend>
                                <div className={'flex flex-col gap-3'}>
                                    <label className={'flex items-center gap-2 text-sm text-foreground'}>
                                        <input
                                            type={'radio'}
                                            name={'individual-limit'}
                                            value={'limited'}
                                            checked={limitChoice === 'limited'}
                                            onChange={() => setLimitChoice('limited')}
                                            className={'accent-primary'}
                                        />
                                        <span>{t('master.tenants.dialog.limit_limited_prefix')}</span>
                                        {/* Text, not number: "-" or "e" must reach the check and show
                                            the inline message instead of silently reading as empty. */}
                                        <input
                                            type={'text'}
                                            inputMode={'numeric'}
                                            value={limitText}
                                            disabled={limitChoice !== 'limited'}
                                            aria-label={t('master.tenants.dialog.limit_count')}
                                            aria-invalid={limitInvalid}
                                            onChange={(e) => setLimitText(e.target.value)}
                                            className={limitInputClass}
                                        />
                                        <span>{t('master.tenants.dialog.limit_limited_suffix')}</span>
                                    </label>
                                    <label className={'flex items-center gap-2 text-sm text-foreground'}>
                                        <input
                                            type={'radio'}
                                            name={'individual-limit'}
                                            value={'unlimited'}
                                            checked={limitChoice === 'unlimited'}
                                            onChange={() => {
                                                // No number left standing beside "Unbegrenzt".
                                                setLimitChoice('unlimited');
                                                setLimitText('');
                                            }}
                                            className={'accent-primary'}
                                        />
                                        <span>{t('master.tenants.dialog.limit_unlimited')}</span>
                                    </label>
                                </div>
                                {limitInvalid && (
                                    <p className={'mt-1 text-xs text-foreground font-medium'} role={'alert'}>
                                        {t('master.tenants.dialog.limit_invalid')}
                                    </p>
                                )}
                                {/* "Vertrag ändern" on an individual contract: what it was, as a
                                    hint only — the new contract is still an explicit choice. */}
                                {tenant.contract?.tier === 'individual' && (
                                    <p className={'mt-2 text-xs text-foreground/60'}>
                                        {typeof tenant.contract.objectLimit === 'number'
                                            ? t('master.tenants.dialog.limit_current_limited', { count: tenant.contract.objectLimit })
                                            : t('master.tenants.dialog.limit_current_unlimited')}
                                    </p>
                                )}
                            </fieldset>
                        )}
                        <InputField
                            label={t('master.tenants.dialog.offer_number') + ' *'}
                            value={offerNumber}
                            onChange={setOfferNumber}
                            maxLength={OFFER_NUMBER_MAX}
                            labelAbove
                        />
                        <SingleDatePicker
                            label={t('master.tenants.dialog.accepted_on') + ' *'}
                            value={acceptedOn ? dayjs(acceptedOn) : null}
                            onChange={(value) => setAcceptedOn(value ? value.format(ISO_DATE) : null)}
                            maxDate={dayjs(today)}
                        />
                        <div>
                            <SingleDatePicker
                                label={t('master.tenants.dialog.starts_on')}
                                value={shownStart ? dayjs(shownStart) : null}
                                onChange={(value) => setStartsOn(value ? value.format(ISO_DATE) : null)}
                                maxDate={dayjs(today)}
                            />
                            <p className={'mt-1 text-xs text-foreground/60'}>{t('master.tenants.dialog.starts_on_hint')}</p>
                        </div>
                        <TextareaField
                            label={t('master.tenants.dialog.note')}
                            value={note}
                            onChange={setNote}
                            rows={3}
                            labelAbove
                        />
                        {error && (
                            <p className={'text-sm text-foreground font-medium'} role={'alert'}>{error}</p>
                        )}
                    </div>
                </div>

                <div className={'px-6 py-4 flex flex-row-reverse gap-3'}>
                    <CustomButton
                        title={t('master.tenants.dialog.save')}
                        onClick={() => { void handleSubmit(); }}
                        disabled={!canSubmit}
                        loading={isLoading}
                    />
                    <CustomButton
                        title={t('common.cancel')}
                        onClick={onClose}
                        isFilled={false}
                    />
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default TenantContractDialog;
